import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, Plus, BarChart3, GitBranch, Table, Search } from 'lucide-react';
import { type PipelineNode, type PipelineNodeField } from '../../services/pipelineNode.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FullScreenTakeover } from '../../components/FullScreenTakeover';
import { NodeDataTable } from '../../components/NodeDataTable';
import { getPipelineNodeIcon } from '../../components/PipelineNodeIcon';

interface NodeDetailSidebarProps {
  availableNodes: PipelineNode[];
  isLoadingNodes: boolean;
  selectedNodeId: string | null;
  onNodeSelect: (nodeId: string) => void;
  selectedNode: PipelineNode | undefined;
  sidebarTitle?: string;
  currentNodeId?: string;
  onInsertRef?: (refName: string) => void;
  onInsertColumn?: (columnName: string) => void;
  onViewStats?: (nodeId: string, field: PipelineNodeField) => void;
  onViewLineage?: (nodeId: string, field: PipelineNodeField) => void;
}

const ENTITY_TYPES = ['DIMENSION', 'DATE_DIMENSION'];

export function NodeDetailSidebar({
  availableNodes,
  isLoadingNodes,
  selectedNodeId,
  onNodeSelect,
  selectedNode,
  sidebarTitle = 'Pipeline Nodes',
  currentNodeId,
  onInsertRef,
  onInsertColumn,
  onViewStats,
  onViewLineage,
}: NodeDetailSidebarProps) {
  const [showDataTable, setShowDataTable] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isCurrentModel = selectedNode?.id === currentNodeId;

  // Split nodes into entities and models, filtered by search
  const { entities, models } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filteredNodes = query
      ? availableNodes.filter(n => (n.label || n.name || '').toLowerCase().includes(query))
      : availableNodes;
    const sortedNodes = [...filteredNodes].sort((a, b) =>
      (a.label || a.name || '').localeCompare(b.label || b.name || '')
    );
    return {
      entities: sortedNodes.filter(n => ENTITY_TYPES.includes(n.node_type)),
      models: sortedNodes.filter(n => !ENTITY_TYPES.includes(n.node_type)),
    };
  }, [availableNodes, searchQuery]);

  const renderNodeButton = (n: PipelineNode) => (
    <button
      key={n.id}
      onClick={() => onNodeSelect(n.id)}
      className={`w-full text-left px-2 py-1 rounded-md text-sm flex items-center gap-2 transition-colors ${
        selectedNodeId === n.id
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent/50 text-foreground'
      }`}
    >
      {getPipelineNodeIcon(n.node_type)}
      <span className="truncate">{n.label || n.name || 'Unnamed Node'}</span>
    </button>
  );

  return (
    <div className="w-96 border-r border-border flex flex-col flex-shrink-0 h-full">
      {/* Top Panel - Node List (60%) */}
      <div className="h-[60%] overflow-auto p-4">
        <div className="flex items-center gap-4 mb-3">
          <h2 className="text-sm font-semibold text-foreground shrink-0">{sidebarTitle}</h2>
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 pl-6 text-xs"
            />
          </div>
        </div>
        {isLoadingNodes ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Entities Section */}
            {entities.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Entities
                </h3>
                <div className="space-y-1">
                  {entities.map(renderNodeButton)}
                </div>
              </div>
            )}

            {/* Models Section */}
            {models.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Models
                </h3>
                <div className="space-y-1">
                  {models.map(renderNodeButton)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Panel - Selected Node Details (40%) */}
      <div className="h-[40%] overflow-auto p-4 border-t border-border">
        {selectedNode ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-1/2 justify-start"
                onClick={() => setShowDataTable(true)}
              >
                <Table className="h-4 w-4 mr-2" />
                View Data
              </Button>
              {isCurrentModel ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-1/2 justify-start"
                  disabled
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {ENTITY_TYPES.includes(selectedNode.node_type || '') ? 'Go to Entity' : 'Go to Model'}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-1/2 justify-start"
                  asChild
                >
                  <Link to={ENTITY_TYPES.includes(selectedNode.node_type || '') ? `/develop/entities/${selectedNode.id}` : `/develop/modeling/${selectedNode.id}`}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {ENTITY_TYPES.includes(selectedNode.node_type || '') ? 'Go to Entity' : 'Go to Model'}
                  </Link>
                </Button>
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2 text-foreground">DBT Ref</h3>
              <div className="flex items-center justify-between px-3 py-2 bg-muted rounded-md">
                <code className="text-sm font-mono">
                  {selectedNode.name || '-'}
                </code>
                {onInsertRef && selectedNode.name && !isCurrentModel && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 -mr-1"
                    onClick={() => onInsertRef(selectedNode.name!)}
                    title="Insert ref at cursor"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2 text-foreground">Columns</h3>
              {selectedNode.fields && selectedNode.fields.length > 0 ? (
                <div className="space-y-1">
                  {selectedNode.fields.map((field) => {
                    const fieldName = field.name || field.label || '-';
                    return (
                      <div
                        key={field.id || field.name}
                        className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-md text-sm"
                      >
                        <span className="truncate flex-1 min-w-0">{fieldName}</span>
                        <div className="flex items-center gap-0.5 shrink-0 -mr-1">
                          {onViewStats && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => onViewStats(selectedNode.id, field as PipelineNodeField)}
                              title="View Stats"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          )}
                          {onViewLineage && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => onViewLineage(selectedNode.id, field as PipelineNodeField)}
                              title="View Lineage"
                            >
                              <GitBranch className="h-4 w-4" />
                            </Button>
                          )}
                          {onInsertColumn && fieldName !== '-' && !isCurrentModel && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => onInsertColumn(fieldName)}
                              title="Insert column at cursor"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No fields available</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">Select a model to view its details</p>
          </div>
        )}
      </div>

      {/* Data Table Takeover */}
      <FullScreenTakeover
        show={showDataTable}
        onHide={() => setShowDataTable(false)}
        title={`Data: ${selectedNode?.label || selectedNode?.name || 'Node'}`}
      >
        {selectedNode && (
          <div className="p-6 h-full">
            <NodeDataTable pipelineNodeId={selectedNode.id} />
          </div>
        )}
      </FullScreenTakeover>
    </div>
  );
}

